import { useEffect, useCallback } from 'react';
import { eventBus, CanvasEvents } from '../utils/eventBus';
import { useNodeStore } from '../stores/nodeStore';
import { useCanvasStore } from '../stores/canvasStore';
import { AppNode, NodeType, NodeStatus, Connection } from '../types';
import type { WorkflowResponse } from '../types/agent';

const NODE_TYPE_MAP: Record<string, NodeType> = {
  PROMPT_INPUT: NodeType.PROMPT_INPUT,
  MEDIA_UPLOAD: NodeType.MEDIA_UPLOAD,
  IMAGE_GENERATOR: NodeType.IMAGE_GENERATOR,
  VIDEO_GENERATOR: NodeType.VIDEO_GENERATOR,
  VIDEO_ANALYZER: NodeType.VIDEO_ANALYZER,
  IMAGE_EDITOR: NodeType.IMAGE_EDITOR,
  AUDIO_GENERATOR: NodeType.AUDIO_GENERATOR,
  TEXT_NODE: NodeType.TEXT_NODE,
  SCRIPT_NODE: NodeType.SCRIPT_NODE,
  VIDEO_COMPOSE: NodeType.VIDEO_COMPOSE,
  PANORAMA_GENERATOR: NodeType.PANORAMA_GENERATOR,
  GROUP: NodeType.GROUP,
};

function mapNodeType(type: string): NodeType {
  return NODE_TYPE_MAP[type] || NodeType.TEXT_NODE;
}

export function useCanvasEvents() {
  const addNode = useNodeStore((s) => s.addNode);
  const addConnection = useNodeStore((s) => s.addConnection);

  const applyWorkflow = useCallback((workflow: WorkflowResponse) => {
    const nodeStore = useNodeStore.getState();
    const existingNodes = nodeStore.nodes;
    const offsetX = existingNodes.length > 0
      ? Math.max(...existingNodes.map(n => n.x)) + 350
      : 100;
    const offsetY = 100;

    const idMapping = new Map<string, string>();

    const newNodes: AppNode[] = workflow.nodes.map((wn, idx) => {
      const newId = `node_${Date.now()}_${idx}`;
      idMapping.set(wn.id, newId);

      const nodeType = mapNodeType(wn.type);

      return {
        id: newId,
        type: nodeType,
        x: wn.position.x + offsetX,
        y: wn.position.y + offsetY,
        title: wn.title,
        status: NodeStatus.IDLE,
        data: {
          prompt: wn.data?.prompt || '',
          model: wn.data?.model,
          ...wn.data,
        },
        inputs: [],
      };
    });

    newNodes.forEach(node => addNode(node));

    setTimeout(() => {
      workflow.connections.forEach(conn => {
        const fromId = idMapping.get(conn.from);
        const toId = idMapping.get(conn.to);
        if (fromId && toId) {
          addConnection(fromId, toId);
        }
      });
    }, 50);

    eventBus.emit(CanvasEvents.WORKFLOW_APPLY, workflow);

    if (workflow.autoExecute) {
      eventBus.emit(CanvasEvents.WORKFLOW_EXECUTED, workflow, idMapping);
    }

    setTimeout(() => {
      eventBus.emit(CanvasEvents.VIEWPORT_FIT);
    }, 200);
  }, [addNode, addConnection]);

  useEffect(() => {
    const unsub = eventBus.on(CanvasEvents.WORKFLOW_APPLY, (workflow: WorkflowResponse) => {
      applyWorkflow(workflow);
    });

    return unsub;
  }, [applyWorkflow]);

  return { applyWorkflow };
}
